import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";

import {
  Container,
  Grid,
  FormControl,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  IconButton,
  Chip,
  Link,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";

import MultipleSelect from "src/components/MultipleSelect";
import FileUploader from "src/components/FileUploader";
import {
  getLabelFilesDataByFileName,
  getLoggedUserEmail,
  getPriorityLevels,
  getUserGroups,
  getUsers,
  StartBatchUpload,
} from "src/Utils";
import Info, { AlertType } from "src/components/Info";
import { getDateAsAFolderName } from "src/content/Utils";

const BUCKET_NAME = "tearetina";
const STEP_FUNCTION_NAME = "BATCH_UPLOAD_SF_ARN";
const CSV_UPLOAD_LOCATION_KEY =
  "public/labeling_uploads/" + getDateAsAFolderName() + "/";

const MainContent = styled(Box)(() => ({
  height: "100%",
  display: "flex",
  flex: 1,
  overflow: "auto",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  margin: "2%",
}));

function BatchUpload(props) {
  const navigate = useNavigate();
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [priority, setPriority] = useState("");

  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [priorityLevels, setPriorityLevels] = useState([]);
  const [selectedUniqueUsers, setSelectedUniqueUsers] = useState([]);
  const [totalFileRecords, setTotalFileRecords] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFileExt, setSelectedFileExt] = useState("");
  const [alreadyAssignedUsersToFile, setAlreadyAssignedUsersToFile] = useState(
    []
  );

  const [invokeBatchUploadPendingStatus, setInvokeBatchUploadPendingStatus] =
    useState(false);
  const [invokeBatchUploadSuccessStatus, setInvokeBatchUploadSuccessStatus] =
    useState(false);

  const [validateFilePending, setValidateFilePending] = useState(false);
  const [validateFileSucceeded, setValidateFileSucceeded] = useState(false);

  //error states
  const [assignedUsersError, setAssignedUsersError] = useState(false);
  const [priorityError, setPriorityError] = useState(false);
  const [fetchPrioritiesError, setFetchPrioritiesError] = useState(false);
  const [fetchUsersError, setFetchUsersError] = useState(false);
  const [fetchUserGroupsError, setFetchUserGroupsError] = useState(false);
  const [uniqueUsersError, setUniqueUsersError] = useState(false);
  const [parseFileError, setParseFileError] = useState(false);
  const [emptyFileError, setEmptyFileError] = useState(false);
  const [InvokeBatchUploadError, setInvokeBatchUploadError] = useState(false);
  const [columnNameError, setColumnNameError] = useState(false);
  const [sequentialError, setSequentialIdError] = useState(false);
  const [alreadyAssignedFileError, setAlreadyAssignedFileError] =
    useState(false);
  const [fetchLabelByLabelNameError, setFetchLabelByLabelNameError] =
    useState(false);

  const naviagteToUserGroupCreation = (event) => {
    event.preventDefault();
    console.log(event);
    navigate("/user-management/register-user-group");
  };

  const handleUserChange = (event) => {
    setInvokeBatchUploadSuccessStatus(false);
    setAssignedUsersError(false);
    setAlreadyAssignedFileError(false);

    const selectedUsersList = event.target.value;
    const selectedUserNames = selectedUsersList.map((c) => c.name);
    const groupsToBeSelected = selectedGroups.filter((group) => {
      return group.users.every((user) => selectedUserNames.includes(user.name));
    });

    setSelectedGroups(groupsToBeSelected);
    setSelectedUsers(selectedUsersList);
  };

  const handleUserGroupChange = (event) => {
    setInvokeBatchUploadSuccessStatus(false);
    setAssignedUsersError(false);
    setAlreadyAssignedFileError(false);

    const selectedGroupList = event.target.value;
    const usersSetFromSelectedGroups = new Set(
      selectedGroupList.flatMap((groups) =>
        groups.users.map((user) => user.name)
      )
    );
    const selectedUsersByGroup = users.filter((user) =>
      usersSetFromSelectedGroups.has(user.name)
    );

    setSelectedGroups(selectedGroupList);
    setSelectedUsers(selectedUsersByGroup);
  };

  const handlePriorityChange = (event) => {
    setInvokeBatchUploadSuccessStatus(false);
    setPriorityError(false);
    const selectedPriority = event.target.value;
    setPriority(selectedPriority);
  };

  const handleUniqueUserUnselect = (unselectedUser) => {
    setAlreadyAssignedFileError(false);
    setSelectedUniqueUsers((prev) => prev.filter((u) => u !== unselectedUser));
    setSelectedUsers((prev) => prev.filter((u) => u.name !== unselectedUser));
    setSelectedGroups((prev) =>
      prev.filter(
        (group) => !group.users.some((user) => user.name === unselectedUser)
      )
    );
  };

  const hasNotFileAlreadyAssigned = async (file: File) => {
    try {
      const params = {
        filename: file.name,
      };
      const result = await getLabelFilesDataByFileName(params);
      const labelFileData = JSON.parse(
        result["data"].getLabelFilesByFileName.body
      );
      const labelFiles = labelFileData.map((file) => {
        return {
          filename: file.filename,
          users: file.assigned_users,
        };
      });

      if (labelFiles.length == 0) {
        return true;
      } else {
        let hasAssignedUser = false;
        const alreadyAssignedUsers = [];

        for (const file of labelFiles) {
          const { users } = file;
          const matchedUsers = selectedUniqueUsers.filter((user) =>
            users.includes(user)
          );
          if (matchedUsers.length > 0) {
            hasAssignedUser = true;
            alreadyAssignedUsers.push(...matchedUsers);
          }
        }

        if (hasAssignedUser) {
          setAlreadyAssignedUsersToFile(alreadyAssignedUsers);
          setAlreadyAssignedFileError(true);
          return false;
        } else {
          return true;
        }
      }
    } catch (error) {
      setFetchLabelByLabelNameError(true);
      console.error("Error in fetching label files by filename :  ", error);
      return false;
    }
  };

  const validateFileUpload = async (file: File) => {
    setAlreadyAssignedUsersToFile([]);

    let isValidationSucceded = await hasNotFileAlreadyAssigned(file);

    if (selectedUniqueUsers.length == 0) {
      isValidationSucceded = false;
      setAssignedUsersError(true);
    }
    if (!priority) {
      isValidationSucceded = false;
      setPriorityError(true);
    }

    if (
      fetchPrioritiesError ||
      fetchUserGroupsError ||
      fetchUsersError ||
      parseFileError ||
      columnNameError ||
      sequentialError ||
      fetchLabelByLabelNameError
    ) {
      isValidationSucceded = false;
    }

    return isValidationSucceded;
  };

  const obtainFileData = (file: File) => {
    setInvokeBatchUploadSuccessStatus(false);
    setInvokeBatchUploadError(false);
    setEmptyFileError(false);
    setParseFileError(false);
    setColumnNameError(false);
    setSequentialIdError(false);
    setValidateFileSucceeded(false);
    setAlreadyAssignedFileError(false);

    setValidateFilePending(true);
    console.log("incomig file : ", file);
    try {
      setSelectedFileName(file.name); // grabbing the file name
      setSelectedFileExt(file.name.split(".").pop());
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const rows = results.data;
          setTotalFileRecords(rows.length);

          if (rows.length === 0) {
            setEmptyFileError(true);
            setValidateFilePending(false);
            return;
          }

          // Validate column names
          const headers = results.meta.fields;
          const requiredHeaders = [
            "Id",
            "LOTA",
            "LOTB",
            "mainQuestion",
            "questionRows",
            "answerColumns",
          ];
          const missingHeaders = requiredHeaders.filter(
            (header) => !headers.includes(header)
          );

          if (missingHeaders.length > 0) {
            setColumnNameError(true);
            setValidateFilePending(false);
            return;
          }

          const ids = rows
            .map((row) => parseInt(row.BatchItemId, 10))
            .filter((id) => !isNaN(id));

          const isSequential = ids.every(
            (id, index) => index === 0 || id === ids[index - 1] + 1
          );
          if (!isSequential) {
            setSequentialIdError(true);
            setValidateFilePending(false);
            return;
          }

          setValidateFilePending(false);
          setValidateFileSucceeded(true);
        },
        skipEmptyLines: true,
      });
    } catch (error) {
      setValidateFilePending(false);
      setParseFileError(true);
      console.error("Error in file parsing!", error);
    }
  };

  const InvokeBatchUploadWorkflow = async (file: File) => {
    try {
      setInvokeBatchUploadPendingStatus(true);
      const params = {
        step_function: STEP_FUNCTION_NAME,
        batch_data: {
          filename: file.name,
          priority_level: priority,
          assigned_users: selectedUniqueUsers,
          initiated_by: await getLoggedUserEmail(),
          initiated_at: new Date(),
          bucket: BUCKET_NAME,
          key: CSV_UPLOAD_LOCATION_KEY + file.name,
          total_records: totalFileRecords,
        },
      };

      await StartBatchUpload(params);

      // resetting the states
      setSelectedUniqueUsers([]);
      setSelectedGroups([]);
      setSelectedUsers([]);
      setPriority("");
      setSelectedFileName("");
      setTotalFileRecords(0);
      setValidateFileSucceeded(false);

      setInvokeBatchUploadPendingStatus(false);
      setInvokeBatchUploadSuccessStatus(true);
    } catch (error) {
      setInvokeBatchUploadPendingStatus(false);
      setInvokeBatchUploadError(true);
      console.log("Error in invoking the batch upload", error);
    }
  };

  useEffect(() => {
    const handleUniqueUsers = () => {
      try {
        const usersFromUsers = selectedUsers.map((u) => u.name);
        const usersFromGroups = selectedGroups.flatMap((ug) =>
          ug.users.map((u) => u.name)
        );
        const uniqueUsers = [
          ...new Set([...usersFromUsers, ...usersFromGroups]),
        ];
        setSelectedUniqueUsers(uniqueUsers);
      } catch (error) {
        setUniqueUsersError(true);
        console.error("Error in handling the unique users : ", error);
      }
    };

    handleUniqueUsers();
  }, [selectedGroups, selectedUsers]);

  useEffect(() => {
    if (users.length === 0) {
      const fetchUsers = async () => {
        try {
          const usersList = await getUsers();

          console.log("hi:", usersList)

          if (usersList["data"] && usersList["data"].listUsers.length > 0) {
            // omit setUsers if no users are returned.
            const convertedList = usersList["data"].listUsers.map((user) => {
              const getAttributeValue = (attrName) =>
                user.Attributes.find((attr) => attr.Name === attrName)?.Value ||
                "";

              return {
                id: getAttributeValue("email"),
                name: getAttributeValue("email"),
              };
            });
            setUsers(convertedList);
          }
        } catch (error) {
          console.error("Error fetching users:", error);
          setUsers([]);
        }
      };

      fetchUsers();
    }
  }, [users]);

  useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        const result = await getUserGroups();
        const userGroupData = JSON.parse(result["data"].listUserGroups.body);
        const userGroupList = userGroupData.map((userGroup) => {
          return {
            id: userGroup._id.$oid,
            name: userGroup.name,
            users: userGroup.users,
          };
        });
        setUserGroups(userGroupList);
      } catch (error) {
        setFetchUserGroupsError(true);
        console.error("Error in fetching user group list : ", error);
      }
    };

    fetchUserGroups();
  }, []);

  useEffect(() => {
    const fetchPriorityLevels = async () => {
      try {
        const result = await getPriorityLevels();
        const priorityData = JSON.parse(result["data"].listPriorities.body);
        const priorities = priorityData.map((priority) => {
          return {
            id: priority.id,
            name: priority.name,
          };
        });
        setPriorityLevels(priorities);
      } catch (error) {
        setFetchPrioritiesError(true);
        console.error("Error in fetching priorities : ", error);
      }
    };
    fetchPriorityLevels();
  }, []);

  return (
    <>
      <Helmet>
        <title>{props.props.title}</title>
      </Helmet>
      <MainContent>
        <Container>
          {fetchUsersError && (
            <>
              <Info
                message="Error in fetching users. please try again later or contact support!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {fetchUserGroupsError && (
            <>
              <Info
                message="Error in fetching user groups. please try again later or contact support!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {fetchPrioritiesError && (
            <>
              <Info
                message="Error in fetching priorities. please try again later or contact support!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {uniqueUsersError && (
            <>
              <Info
                message="Error in handling unique users. please try again later or contact support!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {columnNameError && (
            <>
              <Info
                message="Error in file headers. please check all the column names and submit again!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {sequentialError && (
            <>
              <Info
                message="Id column values are not in sequential order. please check the file and submit again!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {emptyFileError && (
            <>
              <Info
                message="The file you have selected is empty. please check the file and submit again!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {InvokeBatchUploadError && (
            <>
              <Info
                message="Invoking the batch upload has failed. please try again later or contact support!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {invokeBatchUploadPendingStatus && (
            <>
              <Info
                message="Please wait until the data fetching workflow gets started!"
                infoType={AlertType.WARNING}
              />
            </>
          )}
          {fetchLabelByLabelNameError && (
            <>
              <Info
                message="Unable to check whether file has already assigned to selected users. contact support if the issue persits!"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {alreadyAssignedFileError && (
            <>
              <Info
                message={`This file has been already assigned to the following users : ${alreadyAssignedUsersToFile}`}
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {invokeBatchUploadSuccessStatus && (
            <Info
              message={
                <>
                  Data fetching workflow started successfully! Visit
                  <Link
                    href="/audit/view-upload-tasks"
                    color="primary"
                    underline="hover"
                  >
                    {" " + "Audit Operations" + " "}
                  </Link>
                  to Check the Progress.
                </>
              }
              infoType={AlertType.SUCCESS}
            />
          )}
          <Typography textAlign="left" variant="h3" marginY="2%" width="100%">
            Label File Upload
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            fontWeight="normal"
            marginBottom="1%"
          >
            Select Users and Groups need to be assgined to the Label File.
          </Typography>
          <Grid container spacing={2} marginBottom="2%">
            <Grid
              item
              xs={12}
              sm={3.75}
              container
              justifyContent="center"
              alignItems="center"
            >
              <MultipleSelect
                items={userGroups}
                selectedItems={selectedGroups}
                handleChange={handleUserGroupChange}
                label="Select an User Group"
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={3.75}
              container
              justifyContent="center"
              alignItems="center"
            >
              <MultipleSelect
                items={users}
                selectedItems={selectedUsers}
                handleChange={handleUserChange}
                label="Select an User"
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={3.75}
              container
              justifyContent="center"
              alignItems="center"
            >
              <FormControl required fullWidth>
                <InputLabel id="priority-level-label">
                  Priority Level
                </InputLabel>
                <Select
                  labelId="priority-level-label"
                  id="priority-level"
                  value={priority}
                  label="Priority Level *"
                  onChange={handlePriorityChange}
                >
                  {priorityLevels &&
                    priorityLevels.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={0.75}
              container
              justifyContent="center"
              alignItems="center"
            >
              <IconButton
                onClick={naviagteToUserGroupCreation}
                size="large"
                sx={{ padding: "1rem", backgroundColor: "#eaeaea" }}
              >
                <GroupAddIcon color="primary" />
              </IconButton>
            </Grid>
          </Grid>

          {selectedUniqueUsers && (
            <>
              <Box marginBottom="2%">
                {selectedUniqueUsers.map((user) => (
                  <Chip
                    key={user}
                    label={user}
                    onDelete={() => handleUniqueUserUnselect(user)}
                    color="primary"
                    variant="outlined"
                    sx={{ margin: "2px" }}
                  />
                ))}
              </Box>
            </>
          )}
          {assignedUsersError && (
            <>
              <Info
                message="Need to assign users to the batch"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {priorityError && (
            <>
              <Info
                message="Need to select a priority level for the batch"
                infoType={AlertType.ERROR}
              />
            </>
          )}
          {validateFilePending && (
            <>
              <Info
                message="File Validation in Process..."
                infoType={AlertType.WARNING}
              />
            </>
          )}
          {validateFileSucceeded && (
            <>
              <Info
                message="File Validation Succeeded."
                infoType={AlertType.SUCCESS}
              />
            </>
          )}
          <FileUploader
            keyPath="labeling_uploads"
            execValidationsBeforeUpload={validateFileUpload}
            obtainFileData={obtainFileData}
            execAfterUpload={InvokeBatchUploadWorkflow}
            supportedExtensions={["csv"]}
          />
        </Container>
      </MainContent>
    </>
  );
}

export default BatchUpload;
